import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { blobToText } from '@shared/utils/functions';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, retry, switchMap, take } from 'rxjs/operators';
import { LoaderAndErrorService } from './../services/loading.service';
import { AuthService } from '@shared/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class HttpErrorInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private injector: Injector,
    private loaderAndErrorService: LoaderAndErrorService,
    private translocoService: TranslocoService,
    private authService: AuthService
  ) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next
      .handle(request)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          const toastrService = this.injector.get(ToastrService);
          let errorMessage = '';
          let isBlobResponse = false;

          if (error.error instanceof ErrorEvent) {
            // client-side error
            errorMessage = `Error: ${error.error.message}`;
          } else if (error.error instanceof Blob) {
            // server-side error
            isBlobResponse = true;
            blobToText(error?.error).then((text) => {
              const parsedText = JSON.parse(text);
              errorMessage = parsedText?.title || parsedText?.detail || this.translocoService.translate('HTTP_ERROR_MESSAGES.DEFAULT');
            });
          } else {
            // server-side error
            switch (error.status) {
              case 401:
                // console.log('UnAuthorized error', error);
                // errorMessage = `You are Unauthorized to perform this action`;
                errorMessage = this.translocoService.translate('HTTP_ERROR_MESSAGES.401');
                break;
              case 400:
              case 429:
                if (typeof error.error === 'string') {
                  const parsedError = JSON.parse(error.error)
                  errorMessage = parsedError?.message || parsedError?.title;
                } else {
                  errorMessage = error.error.message || error.error.title;
                }
                break;
              case 404:
                // errorMessage = `The requested resource could not be found`;
                errorMessage = this.translocoService.translate('HTTP_ERROR_MESSAGES.404');
                break;
              case 799:
                break;
              default:
                // console.log('Default Error', error);
                // errorMessage = `An error occurred. We are looking into it!`;
                errorMessage = this.translocoService.translate('HTTP_ERROR_MESSAGES.DEFAULT');
                break;
            }
          }

          if (error.status == 401 || error.status == 403) {
            if (this.authService.refreshToken.trim() == "" || this.authService.refreshToken == null) {
              this.authService.signOut();
              return of(error.message);
            }
            else {
              if (!this.isRefreshing) { // to stop multi calls
                this.isRefreshing = true;
                this.refreshTokenSubject.next(null);
                return this.authService.refreshTokenAction().pipe(
                  switchMap((token: any) => {
                    this.isRefreshing = false;
                    this.authService.accessToken = token.access_token;
                    this.authService.refreshToken = token.refresh_token;
                    const access_token = this.authService.accessToken;
                    this.refreshTokenSubject.next(token.access_token);
                    return next.handle(request.clone({ headers: request.headers.set('Authorization', 'Bearer ' + access_token) }))
                  }), retry(2), catchError(error => {
                    this.isRefreshing = false;
                    this.authService.signOut();
                    this.refreshTokenSubject.complete();
                    this.refreshTokenSubject = new BehaviorSubject<string | null>(null);
                    toastrService.error(errorMessage);
                    this.loaderAndErrorService.setError(errorMessage);
                    return of(error.message);
                  }))
              }


              return this.refreshTokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => {
                  return next.handle(
                    request.clone({
                      headers: request.headers.set('Authorization', 'Bearer ' + token)
                    })
                  );
                })
              );
            }
          }


          if (error.status != 799 && errorMessage) {
            setTimeout(() => {
              toastrService.error(errorMessage);
              this.loaderAndErrorService.setError(errorMessage);
            }, isBlobResponse ? 500 : 0);
          }
          return throwError(error);
          // return of(new HttpResponse({}));
          // this._loading.setError(errorMessage);
        })
      );
  }
}


